import * as React from 'react';

import {
  applySelectedTextUtility,
  applyTextUtilities,
  findActiveTextUtilityContext,
  getFilteredTextUtilityCommands,
} from '~/lib/text-utilities';
import { cn } from '~/lib/utils';

type InputProps = React.ComponentProps<'input'> & {
  enableTextUtilities?: boolean;
};

const NON_TEXT_INPUT_TYPES = new Set([
  'email',
  'number',
  'password',
  'tel',
  'url',
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
  'color',
  'file',
  'hidden',
  'range',
  'search',
]);

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', onChange, onKeyDown, onClick, onKeyUp, enableTextUtilities = true, ...props }, ref) => {
    const shouldApplyUtilities = enableTextUtilities && !NON_TEXT_INPUT_TYPES.has(type);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [currentValue, setCurrentValue] = React.useState<string>(String(props.value ?? props.defaultValue ?? ''));
    const [cursorPosition, setCursorPosition] = React.useState<number>(currentValue.length);
    const [activeCommandIndex, setActiveCommandIndex] = React.useState(0);

    React.useEffect(() => {
      if (typeof props.value === 'string') {
        setCurrentValue(props.value);
      }
    }, [props.value]);

    const utilityContext = React.useMemo(
      () => (shouldApplyUtilities ? findActiveTextUtilityContext(currentValue, cursorPosition) : null),
      [currentValue, cursorPosition, shouldApplyUtilities],
    );
    const filteredCommands = React.useMemo(
      () => (utilityContext ? getFilteredTextUtilityCommands(utilityContext.query) : []),
      [utilityContext],
    );
    const showUtilities = filteredCommands.length > 0 && utilityContext !== null;

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const applyUtility = (token: string) => {
      if (!inputRef.current || !utilityContext) return;
      const applied = applySelectedTextUtility(inputRef.current.value, utilityContext, token);
      if (!applied) return;
      const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      setter?.call(inputRef.current, applied.nextValue);
      inputRef.current.setSelectionRange(applied.nextCursor, applied.nextCursor);
      setCurrentValue(applied.nextValue);
      setCursorPosition(applied.nextCursor);
      setActiveCommandIndex(0);
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      inputRef.current.focus();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (shouldApplyUtilities) {
        const transformed = applyTextUtilities(event.currentTarget.value);
        if (transformed !== event.currentTarget.value) {
          const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
          setter?.call(event.currentTarget, transformed);
        }
      }
      setCurrentValue(event.currentTarget.value);
      setCursorPosition(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
      setActiveCommandIndex(0);
      onChange?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (showUtilities) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActiveCommandIndex((prev) => (prev + 1) % filteredCommands.length);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActiveCommandIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setActiveCommandIndex(0);
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          const selected = filteredCommands[activeCommandIndex] ?? filteredCommands[0];
          if (selected) applyUtility(selected.token);
          return;
        }
      }
      onKeyDown?.(event);
    };

    return (
      <div className="relative w-full">
        <input
          type={type}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            className,
          )}
          ref={setRefs}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={(event) => {
            setCursorPosition(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
            onClick?.(event);
          }}
          onKeyUp={(event) => {
            setCursorPosition(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
            onKeyUp?.(event);
          }}
          {...props}
        />
        {showUtilities ? (
          <div className="absolute left-0 right-0 top-[calc(100%+0.4rem)] z-30 rounded-lg border border-border bg-background shadow-lg overflow-hidden">
            {filteredCommands.map((command, index) => (
              <button
                key={command.token}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  applyUtility(command.token);
                }}
                className={`w-full text-left px-3 py-2 transition-colors ${
                  index === activeCommandIndex ? 'bg-muted' : 'hover:bg-muted/60'
                }`}
              >
                <p className="text-sm font-medium">{command.token}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{command.description}</p>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
